import { Box, Button, Typography } from "@outschool/backpack";
import {
  renderCurrencyStringWithCurrencyConversion,
  useTranslation,
} from "@outschool/localization";
import { Loading } from "@outschool/ui-legacy-component-library";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import React from "react";

import { usePaymentMethods } from "../../hooks/CurrentUser";
import { StripeProvider } from "../payments/Stripe";
import StripeAuthorization from "../payments/StripeAuthorization";
import { StripeEditSavedCard } from "../payments/StripeSavedCards";
import { EnrollmentMeetingPurchaseQueryType } from "./EnrollmentMeetingPurchaseWithStripe";

interface EnrollmentMeetingPayLaterCheckoutProps {
  onCancel: () => void;
  onSubmit: () => void;
  activity: EnrollmentMeetingPurchaseQueryType["activity"];
  dueToday: number;
  totalCents: number;
}

export const EnrollmentMeetingPayLaterCheckout = (
  props: EnrollmentMeetingPayLaterCheckoutProps,
) => {
  const { totalCents = 0 } = props;

  return (
    <StripeProvider amountInCents={totalCents}>
      <CheckoutForm {...props} />
    </StripeProvider>
  );
};

export const CheckoutForm = ({
  dueToday,
  onCancel,
  onSubmit,
  activity,
}: EnrollmentMeetingPayLaterCheckoutProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation("client\\components\\payments\\StripeMessages");

  const { loading: isPaymentMethodsLoading, paymentMethods } =
    usePaymentMethods();
  const [defaultPaymentMethod] = paymentMethods;
  const [isSaveForLater, setIsSaveForLater] = React.useState(false);

  const priceString = renderCurrencyStringWithCurrencyConversion({
    amountUSDCents: dueToday,
    showCurrencyCode: true,
  });

  const disableSubmit =
    !defaultPaymentMethod || !isSaveForLater || isPaymentMethodsLoading;

  const handleSubmit = () => {
    if (!!defaultPaymentMethod) {
      onSubmit();
    } else {
      throw Error("Does not have saved Card to pay regularly");
    }
  };

  const showLoading = isPaymentMethodsLoading || !elements || !stripe;

  if (showLoading) {
    return <Loading />;
  }

  return (
    <Box
      flex
      sx={{
        width: "100%",
        flexDirection: "column",
      }}
    >
      <form onSubmit={handleSubmit}>
        <Box>
          <Box
            sx={{
              paddingTop: "0.5em",
            }}
          >
            <Box>
              {!!defaultPaymentMethod && (
                <Box>
                  <Typography
                    variant="h3"
                    sx={{
                      borderColor: "grey.100",
                      display: "",
                    }}
                  >
                    {t("Saved card")}
                  </Typography>
                  <StripeEditSavedCard
                    defaultPaymentMethod={defaultPaymentMethod}
                    editCallback={() => {}}
                    disabled={true}
                    replaceDescription={t(
                      "Your card will not be charged today. You can change the default payment method any time under your Profile Settings.",
                    )}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        <>
          <StripeAuthorization
            setIsSaveForLater={setIsSaveForLater}
            isSaveForLater={isSaveForLater}
            isPaymentProcessing={false}
            isPaymentSuccessful={false}
            activity={activity}
          />
          <Box
            sx={{
              marginBottom: "1em",
              width: "100%",
            }}
          >
            <Button
              variant="contained"
              data-test-id="payButton"
              disabled={disableSubmit}
              onClick={handleSubmit}
              sx={{
                width: "100%",
              }}
            >
              {t("Pay {{price}}", { price: priceString })}
            </Button>
          </Box>
          <Box
            sx={{
              marginBottom: "1em",
              width: "100%",
            }}
          >
            <Button
              variant="link"
              onClick={onCancel}
              sx={{
                width: "100%",
              }}
            >
              {t("Go Back")}
            </Button>
          </Box>
        </>
      </form>
    </Box>
  );
};
